/**
 * Robot manufacturer registry and factory
 * Central hub for managing different robot manufacturers
 */

import { logger } from '../util/logger';
import { FanucManufacturerPlugin } from './manufacturers/fanuc/fanuc-manufacturer.plugin';
import { UniversalRobotsManufacturerPlugin } from './manufacturers/universal-robots/ur-manufacturer.plugin';
import {
  ManufacturerNameArg,
  ManufacturerPlugin,
  RobotManufacturer,
  RobotRegistryService,
} from './robot.types';
import { getManufacturerByName } from './robot.utils';

/**
 * Registry for all supported robot manufacturers
 */
class RobotRegistryServiceImpl implements RobotRegistryService {
  private manufacturerPlugins = new Map<
    RobotManufacturer,
    ManufacturerPlugin
  >();

  /**
   * Register a manufacturer plugin with the registry.
   */
  public registerPlugin(plugin: ManufacturerPlugin): void {
    const manufacturer = plugin.getManufacturer();
    this.manufacturerPlugins.set(manufacturer, plugin);

    logger.info('registered robot manufacturer plugin', {
      manufacturer,
    });
  }

  /**
   * Get a manufacturer plugin by manufacturer.
   */
  public getPlugin(
    manufacturerName: ManufacturerNameArg
  ): ManufacturerPlugin | undefined {
    const manufacturer = getManufacturerByName(manufacturerName);

    if (!manufacturer) {
      logger.warn('requested plugin for unknown manufacturer', {
        manufacturerName,
      });
      return;
    }

    const plugin = this.manufacturerPlugins.get(manufacturer);

    if (!plugin) {
      logger.warn('requested plugin for unknown manufacturer', {
        manufacturer,
      });
      return;
    }

    return plugin;
  }

  /**
   * Get all registered manufacturers.
   */
  public getManufacturers(): RobotManufacturer[] {
    return Array.from(this.manufacturerPlugins.keys());
  }

  /**
   * Get all models for a specific manufacturer.
   */
  public getModels(
    manufacturerName: ManufacturerNameArg
  ): string[] | undefined {
    const plugin = this.getPlugin(manufacturerName);

    return plugin?.getModels();
  }

  public isValidModel(
    manufacturerName: ManufacturerNameArg,
    options: { modelNumber?: string | null }
  ): boolean {
    const { modelNumber } = options;

    const plugin = this.getPlugin(manufacturerName);

    return plugin?.isValidModel(modelNumber) ?? false;
  }
}

const createRobotRegistryService = (
  plugins: ManufacturerPlugin[]
): RobotRegistryService => {
  const registry = new RobotRegistryServiceImpl();

  plugins.forEach((plugin) => {
    registry.registerPlugin(plugin);
  });

  return registry;
};

export const robotRegistryService = createRobotRegistryService([
  new UniversalRobotsManufacturerPlugin(),
  new FanucManufacturerPlugin(),
]);
